import React, { useCallback, useEffect, useState } from "react";
import { X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import Swal from "sweetalert2";
import { apiUrl } from "../config/Config";

const RAZORPAY_KEY = "rzp_live_T7sfY9QsxMcVm8";
const CHECK_AVAILABILITY_ENDPOINT = "checkcustomerorderavailability";
const CREATE_ORDER_ENDPOINT = "createcustomerorder";
const VERIFY_PAYMENT_ENDPOINT = "verifycustomerorderpayment";
const TEST_RAZORPAY_AMOUNT = 100;
const GST_RATE = 0.18;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const customerdata = useSelector((store) => store.userReducer.customerdata);
  const cartdata = useSelector((store) => store.userReducer.cartdata);
  const cartItems = Array.isArray(cartdata) ? cartdata : [];
  const customerToken = customerdata?.token;
  const [selectedSchedule, setSelectedSchedule] = useState("Express Delivery");
  const [showContactModal, setShowContactModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [contactSaving, setContactSaving] = useState(false);
  const [addressSaving, setAddressSaving] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [contactNumber, setContactNumber] = useState(customerdata?.number || "");
  const [orderNote, setOrderNote] = useState("");
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityError, setAvailabilityError] = useState("");
  const [checkoutPricing, setCheckoutPricing] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [couponMessage, setCouponMessage] = useState("");
  const [couponError, setCouponError] = useState("");
  
  // STATE: To control the display of final pricing and place order section
  const [isAvailable, setIsAvailable] = useState(false); 

  const [addressType, setAddressType] = useState("Billing");
  const [addressName, setAddressName] = useState("");
  const [addressCountry, setAddressCountry] = useState("");
  const [addressCity, setAddressCity] = useState("");
  const [addressState, setAddressState] = useState("");
  const [addressZip, setAddressZip] = useState("");
  const [addressStreet, setAddressStreet] = useState("");

  const cartSubtotal = cartItems.reduce((total, item) => {
    return total + (Number(item?.quantity || 0) * Number(item?.price || 0));
  }, 0);
  const subtotal = Number(checkoutPricing?.subtotal ?? cartSubtotal);
  const estimatedTax = Number((subtotal * GST_RATE).toFixed(2));
  const tax = Number(checkoutPricing?.tax ?? estimatedTax);
  const shipping = Number(checkoutPricing?.shipping ?? 0);
  const discount = Number(checkoutPricing?.discount ?? 0);
  const appliedCoupon = checkoutPricing?.coupon || null;
  const total = Number(checkoutPricing?.total ?? (subtotal + tax + shipping));
  const billingAddress = customerdata?.billingaddress;
  const shippingAddress = customerdata?.shippingaddress;

  const formatCurrency = (amount) => `₹ ${Number(amount || 0).toFixed(2)}`;

  const formatAddress = (address) => {
    if (!address) return "";
    return [
      address?.street,
      address?.city,
      address?.state,
      address?.zip,
      address?.country,
    ].filter(Boolean).join(", ");
  };

  const getAddressLabel = (address, fallback) => {
    return address?.name || address?.title || fallback;
  };

  const getFallbackAddress = () => {
    return [
      customerdata?.address,
      customerdata?.area,
      customerdata?.city,
      customerdata?.state,
      customerdata?.pincode,
    ].filter(Boolean).join(", ");
  };

  const getAuthHeaders = useCallback(() => {
    return {
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + customerToken,
      },
    };
  }, [customerToken]);

  const loadRazorpayScript = () => {
    if (window.Razorpay) return Promise.resolve(true);

    return new Promise((resolve) => {
      const existingScript = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
      if (existingScript) {
        existingScript.addEventListener("load", () => resolve(true));
        existingScript.addEventListener("error", () => resolve(false));
        return;
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const buildOrderPayload = () => {
    return {
      // Razorpay testing toggle: use TEST_RAZORPAY_AMOUNT for testing, or switch to Math.round(total * 100) for the real cart total.
      amount: TEST_RAZORPAY_AMOUNT,
      // amount: Math.round(total * 100),
      currency: "INR",
      subtotal,
      tax,
      shipping,
      total,
      coupon_code: couponCode.trim(),
      order_note: orderNote,
      delivery_schedule: selectedSchedule,
      contact_number: contactNumber || customerdata?.number || "",
      customer_name: customerdata?.fullname || "",
      customer_email: customerdata?.email || "",
      billing_address: billingAddress,
      shipping_address: shippingAddress,
      items: cartItems.map((item) => ({
        id: item?.id,
        product_id: item?.product_id || item?.id,
        name: item?.name || item?.productname || "Product",
        quantity: Number(item?.quantity || 0),
        price: Number(item?.price || 0),
        amount: Number(item?.quantity || 0) * Number(item?.price || 0),
        image: item?.cartimage || item?.image || "",
      })),
    };
  };

  const checkOrderAvailability = async () => {
    const res = await axios.post(
      apiUrl + CHECK_AVAILABILITY_ENDPOINT,
      buildOrderPayload(),
      getAuthHeaders()
    );

    if (res?.data?.status !== "success") {
      throw new Error(res?.data?.message || "Some products are not available");
    }

    return res.data.data;
  };

  const extractOrderDetails = (responseData) => {
    const root = responseData?.data || responseData?.order || responseData || {};
    const order = root?.order || root?.razorpay_order || root;

    return {
      orderId:
        order?.id ||
        order?.order_id ||
        order?.razorpay_order_id ||
        root?.id ||
        root?.order_id ||
        root?.razorpay_order_id,
      amount: Number(order?.amount || root?.amount || Math.round(total * 100)),
      currency: order?.currency || root?.currency || "INR",
      receipt: order?.receipt || root?.receipt,
      backendOrderId:
        root?.backend_order_id ||
        root?.order_db_id ||
        root?.customer_order_id ||
        root?.orderid ||
        null,
    };
  };

  const createBackendOrder = async () => {
    const payload = buildOrderPayload();

    try {
      const res = await axios.post(
        apiUrl + CREATE_ORDER_ENDPOINT,
        payload,
        getAuthHeaders()
      );
      if (res?.data?.status === "success" || res?.data?.order || res?.data?.data) {
        return res.data;
      }
      throw new Error(res?.data?.message || "Order creation failed");
    } catch (error) {
      if (error?.response?.status === 404) {
        throw new Error(
          `Checkout API not found at ${CREATE_ORDER_ENDPOINT}. Please verify the backend route.`
        );
      }
      throw error;
    }
  };

  const verifyPaymentWithBackend = async (paymentPayload) => {
    try {
      const res = await axios.post(
        apiUrl + VERIFY_PAYMENT_ENDPOINT,
        paymentPayload,
        getAuthHeaders()
      );
      if (res?.data?.status === "success" || res?.data?.verified === true) {
        return res.data;
      }
      throw new Error(res?.data?.message || "Payment verification failed");
    } catch (error) {
      if (error?.response?.status === 404) {
        throw new Error(
          `Payment verification API not found at ${VERIFY_PAYMENT_ENDPOINT}. Please verify the backend route.`
        );
      }
      throw error;
    }
  };

  const openAddressModal = (type) => {
    const currentAddress = type === "Shipping" ? shippingAddress : billingAddress;
    setAddressType(type);
    setAddressName(currentAddress?.name || currentAddress?.title || customerdata?.fullname || "");
    setAddressCountry(currentAddress?.country || "India");
    setAddressCity(currentAddress?.city || customerdata?.city || "");
    setAddressState(currentAddress?.state || customerdata?.state || "");
    setAddressZip(currentAddress?.zip || customerdata?.pincode || "");
    setAddressStreet(currentAddress?.street || customerdata?.address || "");
    setShowAddressModal(true);
  };

  const handleAddressTypeChange = (type) => {
    const currentAddress = type === "Shipping" ? shippingAddress : billingAddress;
    setAddressType(type);
    setAddressName(currentAddress?.name || currentAddress?.title || customerdata?.fullname || "");
    setAddressCountry(currentAddress?.country || "India");
    setAddressCity(currentAddress?.city || customerdata?.city || "");
    setAddressState(currentAddress?.state || customerdata?.state || "");
    setAddressZip(currentAddress?.zip || customerdata?.pincode || "");
    setAddressStreet(currentAddress?.street || customerdata?.address || "");
  };

  const handleSaveAddress = async() => {
    if(!addressName || !addressCity || !addressState || !addressZip || !addressStreet) {
      alert("Please fill all required address fields");
      return;
    }

    setAddressSaving(true);
    const data = {
      type: addressType,
      name: addressName,
      country: addressCountry,
      city: addressCity,
      state: addressState,
      zip: addressZip,
      street: addressStreet,
    };

    await axios.post(apiUrl + "updatecustomeraddress", data, getAuthHeaders()).then((res) => {
      if(res.data.status === "success") {
        dispatch({
          type: "customerdata",
          payload: {
            ...customerdata,
            billingaddress: res.data.data?.billingaddress,
            shippingaddress: res.data.data?.shippingaddress,
          },
        });
        setShowAddressModal(false);
      } else {
        alert(res.data.message);
      }
    }).catch((err) => {
      alert(err.message);
    });
    setAddressSaving(false);
  };

  useEffect(() => {
    if (!customerToken) {
      navigate('/login', {
        replace: true,
        state: {
          from: location.pathname,
          redirectTo: location.pathname,
        },
      });
      return;
    }

    axios.post(apiUrl + "getcustomercheckoutdetails", {}, getAuthHeaders()).then((res) => {
      if(res.data.status === "success") {
        dispatch({ type: "customerdata", payload: res.data.data });
        setContactNumber(res.data.data?.number || "");
      }
    }).catch(() => {});
  }, [customerToken, location.pathname, navigate, dispatch, getAuthHeaders]);

  useEffect(() => {
    setIsAvailable(false);
    setCheckoutPricing(null);
    setAvailabilityError("");
    setCouponMessage("");
    setCouponError("");
  }, [cartdata]);
  
  const handleCheckAvailability = async () => {
    if (cartItems.length === 0) {
      alert("Your cart is empty");
      return;
    }

    setCheckingAvailability(true);
    setAvailabilityError("");
    setIsAvailable(false);

    try {
      const pricing = await checkOrderAvailability();
      setCheckoutPricing(pricing);
      setIsAvailable(true);
    } catch (error) {
      setCheckoutPricing(null);
      setAvailabilityError(
        error?.response?.data?.message ||
        error.message ||
        "Some products are unavailable or out of stock."
      );
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleUpdateContact = async() => {
    if (!contactNumber) {
      alert("Please enter contact number");
      return;
    }

    setContactSaving(true);
    await axios.post(apiUrl + "updatecustomercontact", { number: contactNumber }, getAuthHeaders()).then((res) => {
      if(res.data.status === "success") {
        dispatch({
          type: "customerdata",
          payload: {
            ...customerdata,
            number: res.data.data?.number || contactNumber,
          },
        });
        setShowContactModal(false);
      } else {
        alert(res.data.message);
      }
    }).catch((err) => {
      alert(err.message);
    });
    setContactSaving(false);
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponMessage("");
      setCouponError("Please enter a coupon code");
      return;
    }

    setApplyingCoupon(true);
    setCouponMessage("");
    setCouponError("");

    try {
      const pricing = await checkOrderAvailability();
      setCheckoutPricing(pricing);
      setIsAvailable(true);
      setCouponMessage("Coupon applied successfully");
    } catch (error) {
      setCouponError(
        error?.response?.data?.message ||
        error.message ||
        "Coupon could not be applied."
      );
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = async () => {
    setCouponCode("");
    setCouponMessage("");
    setCouponError("");
    setApplyingCoupon(true);

    try {
      const res = await axios.post(
        apiUrl + CHECK_AVAILABILITY_ENDPOINT,
        { ...buildOrderPayload(), coupon_code: "" },
        getAuthHeaders()
      );
      if (res?.data?.status !== "success") {
        throw new Error(res?.data?.message || "Unable to remove coupon");
      }
      setCheckoutPricing(res.data.data);
    } catch (error) {
      setCouponError(
        error?.response?.data?.message ||
        error.message ||
        "Unable to remove coupon."
      );
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (cartItems.length === 0) {
      Swal.fire({
        text: "Your cart is empty",
        icon: "error",
        confirmButtonColor: "rgb(59, 151, 196)",
      });
      return;
    }

    if (!billingAddress || !shippingAddress) {
      Swal.fire({
        text: "Please add billing and shipping address before placing order",
        icon: "error",
        confirmButtonColor: "rgb(59, 151, 196)",
      });
      return;
    }

    if (!contactNumber && !customerdata?.number) {
      Swal.fire({
        text: "Please add a contact number before placing order",
        icon: "error",
        confirmButtonColor: "rgb(59, 151, 196)",
      });
      return;
    }

    setPlacingOrder(true);

    try {
      const pricing = await checkOrderAvailability();
      setCheckoutPricing(pricing);

      const sdkLoaded = await loadRazorpayScript();
      if (!sdkLoaded) {
        throw new Error("Razorpay SDK failed to load");
      }

      const orderResponse = await createBackendOrder();
      const orderDetails = extractOrderDetails(orderResponse);

      if (!orderDetails.orderId) {
        throw new Error("Backend did not return a Razorpay order id");
      }

      const options = {
        key: RAZORPAY_KEY,
        amount: orderDetails.amount,
        currency: orderDetails.currency,
        name: "Tanuraaga",
        description: "Checkout Payment",
        order_id: orderDetails.orderId,
        prefill: {
          name: customerdata?.fullname || getAddressLabel(shippingAddress, ""),
          email: customerdata?.email || "",
          contact: contactNumber || customerdata?.number || "",
        },
        notes: {
          order_note: orderNote || "",
          delivery_schedule: selectedSchedule,
          backend_order_id: orderDetails.backendOrderId || "",
        },
        theme: {
          color: "#d89b60",
        },
        handler: async (response) => {
          try {
            await verifyPaymentWithBackend({
              ...buildOrderPayload(),
              backend_order_id: orderDetails.backendOrderId,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            dispatch({ type: "cartdata", payload: [] });
            Swal.fire({
              text: "Payment successful. Your order has been placed.",
              icon: "success",
              confirmButtonColor: "rgb(59, 151, 196)",
            }).then(() => {
              navigate("/profile");
            });
          } catch (error) {
            Swal.fire({
              text: error?.response?.data?.message || error.message || "Payment succeeded, but verification failed.",
              icon: "warning",
              confirmButtonColor: "rgb(59, 151, 196)",
            });
          } finally {
            setPlacingOrder(false);
          }
        },
        modal: {
          ondismiss: () => {
            setPlacingOrder(false);
          },
        },
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.on("payment.failed", (response) => {
        Swal.fire({
          text:
            response?.error?.description ||
            response?.error?.reason ||
            "Payment failed. Please try again.",
          icon: "error",
          confirmButtonColor: "rgb(59, 151, 196)",
        });
        setPlacingOrder(false);
      });
      razorpayInstance.open();
    } catch (error) {
      setPlacingOrder(false);
      Swal.fire({
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to start payment. Please try again.",
        icon: "error",
        confirmButtonColor: "rgb(59, 151, 196)",
      });
    }
  };

  const renderCartItems = () => {
    if (cartItems.length === 0) {
      return (
        <div className="checkout-page-order-item">
          <p className="checkout-page-order-item-name">Your cart is empty</p>
          <span>{formatCurrency(0)}</span>
        </div>
      );
    }

    return cartItems.map((item, index) => (
      <div className="checkout-page-order-item" key={item?.id || item?.product_id || index}>
        <p className="checkout-page-order-item-name">
          {Number(item?.quantity || 0)} × {item?.name || item?.productname || "Product"}
        </p>
        <span>{formatCurrency(Number(item?.quantity || 0) * Number(item?.price || 0))}</span>
      </div>
    ));
  };

  const schedules = [
    { title: "Express Delivery", desc: "90 min express delivery" },
    { title: "Morning", desc: "8.00 AM - 11.00 AM" },
    { title: "Noon", desc: "11.00 AM - 2.00 PM" },
    { title: "Afternoon", desc: "2.00 PM - 5.00 PM" },
    { title: "Evening", desc: "5.00 PM - 8.00 PM" },
  ];

  return (
   <div>
    
      <div className="checkout-page-container">
        <div className="checkout-page-main-layut">
          
          <div className="checkout-page-left">

          
            <div className="checkout-page-section">
              <div className="checkout-page-section-header">
                <div className="checkout-page-header-left">
                  <div className="checkout-page-number">1</div>
                  <p className="checkout-page-header-tittle">Contact Number</p>
                </div>
                <button
                  className="checkout-page-action-btn"
                  onClick={() => setShowContactModal(true)}
                >
                  + Update
                </button>
              </div>
              <div className="checkout-page-box">
                <div className="checkout-page-phone">
                  {contactNumber || customerdata?.number || "No contact number added"}
                </div>
              </div>
            </div>

            {/* BILLING ADDRESS */}
            <div className="checkout-page-section">
              <div className="checkout-page-section-header">
                <div className="checkout-page-header-left">
                  <div className="checkout-page-number">2</div>
                  <p className="checkout-page-header-tittle">Billing Address</p>
                </div>
                <button
                  className="checkout-page-action-btn"
                  onClick={() => openAddressModal("Billing")}
                >
                  {billingAddress ? "Edit" : "+ Add"}
                </button>
              </div>
              <div className="checkout-page-billingbox">
                <p className="checkout-page-label">
                  {getAddressLabel(billingAddress, "Billing")}
                </p>
                <p className="checkout-page-box-text">
                  {formatAddress(billingAddress) || getFallbackAddress() || "No billing address added yet"}
                </p>
              </div>
            </div>

            {/* SHIPPING ADDRESS */}
            <div className="checkout-page-section">
              <div className="checkout-page-section-header">
                <div className="checkout-page-header-left">
                  <div className="checkout-page-number">3</div>
                  <p className="checkout-page-header-tittle">Shipping Address</p>
                </div>
                <button
                  className="checkout-page-action-btn"
                  onClick={() => openAddressModal("Shipping")}
                >
                  {shippingAddress ? "Edit" : "+ Add"}
                </button>
              </div>
              <div className="checkout-page-billingbox">
                <p className="checkout-page-label">
                  {getAddressLabel(shippingAddress, "Shipping")}
                </p>
                <p className="checkout-page-box-text">
                  {formatAddress(shippingAddress) || "No shipping address added yet"}
                </p>
              </div>
            </div>

            {/* DELIVERY SCHEDULE */}
            <div className="checkout-page-section">
              <div className="checkout-page-section-header checkout-page-order-text">
                <div className="checkout-page-number">4</div>
                <p className="checkout-page-header-tittle ">Delivery Schedule</p>
              </div>

              <div className="checkout-page-schedule-grid">
                {schedules.map((slot) => (
                  <div
                    key={slot.title}
                    className={`checkout-page-schedule-item ${
                      selectedSchedule === slot.title ? "active" : ""
                    }`}
                    onClick={() => setSelectedSchedule(slot.title)}
                  >
                    <p className="checkoutpage-shedule-tittle">{slot.title}</p>
                    <p className="checkoutpage-shedule-dec">{slot.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* ORDER NOTE */}
            <div className="checkout-page-section">
              <div className="checkout-page-section-header checkout-page-order-text">
                <div className="checkout-page-number">5</div>
                <p className="checkout-page-header-tittle ">Order Note</p>
              </div>
              <textarea
                className="checkout-page-textarea"
                rows="4"
                placeholder="Add your note here..."
                value={orderNote}
                onChange={(e) => setOrderNote(e.target.value)}
              ></textarea>
            </div>
          </div>

          {/* RIGHT SIDE (MODIFIED LOGIC) */}
          <div className="checkout-page-right">
            
            {/* Display this block BEFORE clicking 'Check Availability' */}
            {!isAvailable && (
              <div className="checkout-page-order-summary">
                <p className="checkout-page-order-summary-tittle">Your Order</p>
                {renderCartItems()}
                <hr />
                <div className="checkout-page-summary-row">
                  <span>Sub Total</span> <span>{formatCurrency(subtotal)}</span>
                </div>
                <div className="checkout-page-summary-row">
                  <span>Tax</span> <span>Calculated at checkout</span>
                </div>
                <div className="checkout-page-summary-row">
                  <span>Estimated Shipping</span> <span>Calculated at checkout</span>
                </div>
                <button
                  className="checkout-page-check-btn"
                  onClick={handleCheckAvailability} 
                  disabled={checkingAvailability}
                >
                  {checkingAvailability ? "Checking..." : "Check Availability"}
                </button>
                {availabilityError && (
                  <p className="checkout-page-availability-error">{availabilityError}</p>
                )}
              </div>
            )}

            {/* Display  blocks  'Check Availability'  */}
            {isAvailable && (
              <>
                {/* 1. Final Summary Block (First Block in Image) */}
                <div className="checkout-page-order-summary">
                    {renderCartItems()}
                    <hr />
                    <div className="checkout-page-summary-row">
                        <span>Sub Total</span> <span>{formatCurrency(subtotal)}</span>
                    </div>
                    <div className="checkout-page-summary-row">
                        <span>Tax</span> <span>{formatCurrency(tax)}</span> 
                    </div>
                    <div className="checkout-page-summary-row">
                        <span>Shipping</span> <span>{formatCurrency(shipping)}</span>
                    </div>
                    {discount > 0 && (
                      <div className="checkout-page-summary-row checkout-page-discount-row">
                        <span>Discount{appliedCoupon?.code ? ` (${appliedCoupon.code})` : ""}</span>
                        <span>- {formatCurrency(discount)}</span>
                      </div>
                    )}
                    <div className="checkout-page-coupon-box">
                        <p className="checkout-page-summary-coupon-tittle">Do you have a coupon?</p>
                        <div className="checkout-page-coupon-form">
                          <input
                            className="checkout-page-coupon-input"
                            placeholder="Enter coupon code"
                            value={couponCode}
                            onChange={(event) => {
                              setCouponCode(event.target.value.toUpperCase());
                              setCouponMessage("");
                              setCouponError("");
                            }}
                            disabled={applyingCoupon}
                          />
                          {appliedCoupon ? (
                            <button
                              className="checkout-page-coupon-btn checkout-page-coupon-remove-btn"
                              onClick={handleRemoveCoupon}
                              disabled={applyingCoupon}
                            >
                              Remove
                            </button>
                          ) : (
                            <button
                              className="checkout-page-coupon-btn"
                              onClick={handleApplyCoupon}
                              disabled={applyingCoupon}
                            >
                              {applyingCoupon ? "Applying..." : "Apply"}
                            </button>
                          )}
                        </div>
                        {couponMessage && (
                          <p className="checkout-page-coupon-message">{couponMessage}</p>
                        )}
                        {couponError && (
                          <p className="checkout-page-coupon-error">{couponError}</p>
                        )}
                    </div>
                   
                    {/* 2. Total/Wallet Section (Second Block in Image) */}
                    <div className="checkout-page-total-section">
                        <div className="checkout-page-summary-row total-row">
                            <span>Total</span> <span>{formatCurrency(total)}</span>
                        </div>
                        {/* <div className="checkout-page-wallet-row">
                            <span>Wallet points</span> <span>0</span>
                        </div>
                        <div className="checkout-page-wallet-row">
                            <span>Wallet currency</span> <span>{formatCurrency(0)}</span>
                        </div>
                        <div className="checkout-page-wallet-checkbox">
                            <input type="checkbox" id="useWallet" />
                            <label htmlFor="useWallet">Do you want to use wallet?</label>
                        </div> */}
                    </div>
                    
                    {/* 3. Payment Method Section (Third Block in Image) */}
                    {/* <div className="checkout-page-payment-section">
                        <p className="checkout-page-payment-title">Choose Payment Method</p>
                        <button className="checkout-page-cod-btn">
                            Cash On Delivery
                        </button>
                        <p className="checkout-page-payment-note">
                            Please Pay After You Receive Your Goods!
                        </p>
                    </div> */}

                    <button
                      className="checkout-page-place-order-btn"
                      onClick={handlePlaceOrder}
                      disabled={placingOrder}
                    >
                        {placingOrder ? "Processing..." : "Place Order"}
                    </button>
                    {/* The error text at the bottom */}
                     {/* <p className="checkout-page-error-message">
                        Please select and fill all the fields and place your order
                    </p> */}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      

      {/* MODALS (Unchanged) */}
      {showContactModal && (
        <div className="checkout-page-modal-overlay">
          <div className="checkout-page-modal">
            <div className="checkout-page-modal-header">
              <h3>Update Contact Number</h3>
              <button
                className="checkout-page-modal-close"
                onClick={() => setShowContactModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="checkout-page-modal-body">
              <input
                className="checkout-page-modal-input"
                value={contactNumber}
                onChange={(e) => setContactNumber(e.target.value)}
                placeholder="Enter contact number"
              />
              <button
                className="checkout-page-modal-update-btn"
                onClick={handleUpdateContact}
                disabled={contactSaving}
              >
                {contactSaving ? "Saving..." : "Update Contact"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddressModal && (
        <div
          className="profile-page-modal-backdrop-address"
          onClick={() => setShowAddressModal(false)}
        >
          <div
            className="profile-page-modal-content-address profile-page-address-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="profile-page-modal-close"
              onClick={() => setShowAddressModal(false)}
              aria-label="Close modal"
            >
              <X size={20} />
            </button>
            <h3 className="profile-page-modal-title">
              {(addressType === "Shipping" ? shippingAddress : billingAddress) ? "Update" : "Add New"} {addressType} Address
            </h3>
            <div className="profile-page-address-form">
              <div className="profile-page-form-group">
                <label className="profile-page-input-label">Type</label>
                <div className="profile-page-address-radio-group">
                  <label className="profile-page-address-radio-label">
                    <input
                      type="radio"
                      name="addressType"
                      value="Billing"
                      checked={addressType === "Billing"}
                      onChange={() => handleAddressTypeChange("Billing")}
                    />
                    Billing
                  </label>
                  <label className="profile-page-address-radio-label">
                    <input
                      type="radio"
                      name="addressType"
                      value="Shipping"
                      checked={addressType === "Shipping"}
                      onChange={() => handleAddressTypeChange("Shipping")}
                    />
                    Shipping
                  </label>
                </div>
              </div>

              <div className="profile-page-form-group">
                <label className="profile-page-input-label">Name / Receiver Name</label>
                <input
                  type="text"
                  value={addressName}
                  onChange={(e) => setAddressName(e.target.value)}
                  placeholder="Enter receiver name"
                />
              </div>

              <div className="profile-page-form-group-row">
                <div className="profile-page-form-group-half">
                  <label className="profile-page-input-label">Country</label>
                  <input
                    type="text"
                    value={addressCountry}
                    onChange={(e) => setAddressCountry(e.target.value)}
                  />
                </div>
                <div className="profile-page-form-group-half">
                  <label className="profile-page-input-label">City</label>
                  <input
                    type="text"
                    value={addressCity}
                    onChange={(e) => setAddressCity(e.target.value)}
                  />
                </div>
              </div>

              <div className="profile-page-form-group-row">
                <div className="profile-page-form-group-half">
                  <label className="profile-page-input-label">State</label>
                  <input
                    type="text"
                    value={addressState}
                    onChange={(e) => setAddressState(e.target.value)}
                  />
                </div>
                <div className="profile-page-form-group-half">
                  <label className="profile-page-input-label">ZIP</label>
                  <input
                    type="text"
                    value={addressZip}
                    onChange={(e) => setAddressZip(e.target.value)}
                  />
                </div>
              </div>

              <div className="profile-page-form-group">
                <label className="profile-page-input-label">Street Address</label>
                <textarea
                  rows="4"
                  value={addressStreet}
                  onChange={(e) => setAddressStreet(e.target.value)}
                ></textarea>
              </div>

              <button
                className="profile-page-save-btn profile-page-address-update-btn"
                onClick={handleSaveAddress}
                disabled={addressSaving}
              >
                {addressSaving ? "Saving..." : "Update Address"}
              </button>
            </div>
          </div>
        </div>
      )}

   
   </div>
  );
};

export default CheckoutPage;
